import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { useResizeObserver } from 'usehooks-ts';

import { Input, Form, Table, Button, Tag, Modal, Row, Col, Space, Select, Flex, notification, Tooltip, InputNumber } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
    LoadingOutlined,
    ReloadOutlined,
    QuestionCircleOutlined,
    CheckOutlined,
    PlusOutlined,
    ForkOutlined,
    RollbackOutlined,
} from '@ant-design/icons';

import IBaseComponentProps from '@components/base-component-props';

import { Tags, TransitWarehouseTag } from '@controls/tags';
import Toolbar from '@controls/toolbar/toolbar';
import FormHeader from '@controls/form-header/form-header';
import Filter from '@controls/filter/filter';

import { delayAction, getEnumList, getDateTimeLocal } from '@extensions/utils';
import { error, exception, securityRestriction } from '@extensions/notification';
import { userLoaded, setFilter } from '@store/actions';

import { useAppDispatch, useAppSelector } from '@store/hooks';

import { IUserSession } from '@entities/user-session';
import { IShipmentJournalLine } from '@entities/shipment-journal-line';
import { IJournalLineFilter } from '@entities/journal-line-filter';
import { IShipmentJournal } from '@entities/shipment-journal';

import { JournalStatus, enumLabel as journalStatusLabel } from '@enums/journal-status';
import { Permission, hasPermission } from '@enums/permission';
import { DeliveryOption, enumLabel as deliveryOptionLabel } from '@enums/delivery-option';
import { IEnumItem } from '@enums/enum-item';

import { ThermometerIcon, ThermometerInIcon, ThermometerOutIcon } from '@icons/index';

dayjs.extend(utc);

interface IShipmentHeader {
    cityId: string;
    cityName: string;
    tag: string;
    totalBoxQty: number;
    totalScannedBoxQty: number;
    totalVolume: number;
    lines: Array<IShipmentJournalLine>;
    tempOut?: number;
}

interface ITempParams {
    tempOut: number;
}

const filterContext: string = 'ShipmentJournal';

const ShipmentJournal = (props: IBaseComponentProps) => {
    const { serverFetch } = props;

    const { id } = useParams();

    const [tempOutForm] = Form.useForm();
    const [deleteLinesForm] = Form.useForm();

    const containerRef = useRef<HTMLDivElement>(null);
    const { width = 0, height = 0 } = useResizeObserver({
        ref: containerRef,
        box: 'border-box',
    });

    const filterRef = useRef<HTMLDivElement>(null);
    const { width: filterWidth = 0, height: filterHeight = 0 } = useResizeObserver({
        ref: filterRef,
        box: 'border-box',
    });

    const initFilter: IJournalLineFilter = { journalIds: [id as string] };

    const { TextArea } = Input;

    const userSession = useAppSelector<IUserSession>((s) => s.userSession);
    const filter = useAppSelector<IJournalLineFilter>((s) => s.filters[filterContext]);

    const [form] = Form.useForm();

    const [modal, modalContextHolder] = Modal.useModal();

    const d = useAppDispatch();
    const [api, contextHolder] = notification.useNotification();
    const navigate = useNavigate();

    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

    const [deliveryOptions] = useState<Array<IEnumItem>>(getEnumList(DeliveryOption, deliveryOptionLabel));
    const [entity, setEntity] = useState<IShipmentJournal>();
    const [headers, setHeaders] = useState<Array<IShipmentHeader>>([]);
    const [showFilter, setShowFilter] = useState<boolean>(true);
    const [refreshRequired, setRefreshRequired] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [openTempOut, setOpenTempOut] = useState<boolean>(false);

    const [openDeleteLinesModal, setOpenDeleteLinesModal] = useState<boolean>(false);

    useEffect(() => {
        let cleanup = false;

        if (!refreshRequired) return;

        const fetchData = async () => {
            setLoading(true);

            const promises = [
                await serverFetch(`shipmentjournals/${id}`, { method: 'GET' })
                    .then((data: any) => {
                        return data;
                    })
                    .catch((ex: any) => {
                        exception(api, 'Ошибка получения журнала  отправки', ex, () => d(userLoaded(undefined)));
                    }),
                await serverFetch(`shipmentjournals/lines`, { method: 'GET', queryParams: filter })
                    .then((data: any) => {
                        return data;
                    })
                    .catch((ex: any) => {
                        exception(api, 'Ошибка получения деталей отправки', ex, () => d(userLoaded(undefined)));
                    }),
            ];

            Promise.all([promises]).then((result) => {
                if (cleanup) return;

                setEntity(result[0][0]);
                setHeaders(result[0][1]);

                setLoading(false);
                setRefreshRequired(false);
            });
        };

        fetchData();

        return () => {
            cleanup = true;
        };
    }, [refreshRequired]);

    useEffect(() => {
        form.setFieldsValue({
            journalNumber: entity?.journalNumber,
            truckNumber: entity?.truckNumber,
            status: entity && journalStatusLabel(entity.status),
        });
    }, [entity]);

    const onSetToProcess = () => {
        serverFetch(`shipmentjournals/toprocess/${id}`, { method: 'POST' })
            .then(() => {
                setRefreshRequired(true);
            })
            .catch((ex: any) => {
                exception(api, 'Ошибка изменения статуса журнала', ex, () => d(userLoaded(undefined)));
            });
    };

    const onDeleteLines = (params?: any) => {
        const data = { lineIds: selectedRowKeys, reason: params?.reason };

        serverFetch(`shipmentjournals/lines/delete`, { method: 'POST', bodyData: data })
            .then(() => {
                setRefreshRequired(true);
                setOpenDeleteLinesModal(false);
            })
            .catch((ex: any) => {
                exception(api, 'Ошибка возврата коробок на остатки', ex, () => d(userLoaded(undefined)));
            });
    };

    const onShipBoxes = () => {
        setLoading(true);

        serverFetch(`shipmentjournals/shipboxes`, { method: 'POST', bodyData: selectedRowKeys })
            .then(() => {
                setLoading(false);
                setRefreshRequired(true);
            })
            .catch((ex: any) => {
                setLoading(false);
                exception(api, 'Ошибка отгрузки коробок', ex, () => d(userLoaded(undefined)));
            });
    };

    const onCompleteShipment = () => {
        setLoading(true);

        serverFetch(`warehouse/shipment/${id}/complete`, { method: 'POST' })
            .then(() => {
                setLoading(false);
                navigate(-1);
            })
            .catch((ex: any) => {
                setLoading(false);
                exception(api, 'Ошибка завершения журнала', ex, () => d(userLoaded(undefined)));
            });
    };

    const onSetDeliveryOption = (option: DeliveryOption) => {
        serverFetch(`shipmentJournals/deliveryoption/${id}/${option}`, { method: 'POST' })
            .then(() => {
                if (entity) setEntity({ ...entity, deliveryOption: option });

                modal.info({
                    title: `Способ доставки изменен на "${deliveryOptionLabel(option)}"`,
                    onOk() {},
                });
            })
            .catch((ex: any) => {
                setLoading(false);
                exception(api, 'Ошибка изменения способа доставки', ex, () => d(userLoaded(undefined)));
            });
    };

    const onSetTemIn = (data: ITempParams) => {
        serverFetch(`shipmentJournals/${entity?.id}/tempout`, { method: 'POST', bodyData: data.tempOut })
            .then(() => {
                setRefreshRequired(true);
                setOpenTempOut(false);
            })
            .catch((ex: any) => {
                exception(api, 'Ошибка сохранения температуры отгружаемого груза', ex, () => d(userLoaded(undefined)));
            });
    };

    useEffect(() => {
        if (!filter || !filter.journalIds?.find((t) => t === id)) {
            d(setFilter(initFilter, filterContext));
            return;
        }

        delayAction(() => setRefreshRequired(true));
    }, [filter]);

    const renderToolbar = () => {
        return (
            <Toolbar
                commands={[
                    {
                        label: 'Обновить',
                        key: 'refresh',
                        disabled: loading,
                        icon: <ReloadOutlined />,
                        onClick: () => {
                            setRefreshRequired(true);
                        },
                    },
                    {
                        label: 'Отгрузить коробки',
                        key: 'ship',
                        disabled: loading || selectedRowKeys.length <= 0 || entity?.status !== JournalStatus.InProcess,
                        type: 'primary',
                        icon: <CheckOutlined />,
                        onClick: () => {
                            if (!hasPermission(userSession.permissions, Permission.ShipBoxManual)) {
                                securityRestriction(api, [Permission.ShipBoxManual]);
                                return;
                            }

                            modal.confirm({
                                title: `Отгрузить выбранные коробки?`,
                                okType: 'primary',
                                icon: <QuestionCircleOutlined />,
                                okText: 'ОК',
                                cancelText: 'Отмена',
                                onOk: () => {
                                    onShipBoxes();
                                },
                            });
                        },
                    },
                    {
                        label: 'Добавить коробки',
                        key: 'add',
                        disabled: loading || entity?.status === JournalStatus.Completed || entity?.status === JournalStatus.Cancelled,
                        icon: <PlusOutlined />,
                        onClick: () => {
                            if (!hasPermission(userSession.permissions, Permission.ManageWarehouse)) {
                                securityRestriction(api, [Permission.ManageWarehouse]);
                                return;
                            }

                            navigate(`/warehouse/remainings/shipment/${id}`);
                        },
                    },
                    {
                        label: 'Вернуть на остатки',
                        key: 'delete',
                        disabled: loading || selectedRowKeys.length <= 0 || entity?.status === JournalStatus.Cancelled,
                        icon: <RollbackOutlined />,
                        onClick: () => {
                            if (!hasPermission(userSession.permissions, Permission.DeleteShipmentBox)) {
                                securityRestriction(api, [Permission.DeleteShipmentBox]);
                                return;
                            }

                            modal.confirm({
                                title: `Вернуть выбранные коробки на остатки?`,
                                icon: <QuestionCircleOutlined />,
                                okType: 'primary',
                                okText: 'Вернуть',
                                cancelText: 'Отмена',
                                onOk: () => {
                                    if (entity?.status == JournalStatus.Completed) {
                                        setOpenDeleteLinesModal(true);
                                    } else {
                                        onDeleteLines();
                                    }
                                },
                            });
                        },
                    },
                    { isDivider: true },
                    {
                        label: 'Температура принимаего груза',
                        key: 'tempIn',
                        disabled: loading,
                        variant: 'solid',
                        color: 'purple',
                        icon: <ThermometerIcon />,
                        onClick: () => {
                            if (!hasPermission(userSession.permissions, Permission.ManageWarehouse)) {
                                securityRestriction(api, [Permission.ManageWarehouse]);
                                return;
                            }
                            setOpenTempOut(true);
                        },
                    },
                    {
                        label: 'Управление маршрутом',
                        key: 'manageRoutes',
                        disabled: loading || !id || !entity || entity?.status === JournalStatus.Cancelled,
                        icon: <ForkOutlined />,
                        onClick: () => {
                            if (!hasPermission(userSession.permissions, Permission.ManageWarehouse)) {
                                securityRestriction(api, [Permission.ManageWarehouse]);
                                return;
                            }

                            navigate(`/warehouse/shipmentjournals/manageoutes/${entity?.id}/${entity?.journalNumber}`);
                        },
                    },
                ]}
                filter={{
                    active: showFilter,
                    onHideFilter: () => {
                        setShowFilter(!showFilter);
                    },
                }}
            />
        );
    };

    const renderFilter = () => {
        return (
            <div ref={filterRef}>
                <Filter
                    display={showFilter}
                    items={[
                        <Input
                            style={{ width: 130 }}
                            key='boxNumber'
                            placeholder='Коробка'
                            value={filter?.boxNumber}
                            onChange={(data) => {
                                d(setFilter({ ...filter, boxNumber: data.target.value }, filterContext));
                            }}
                        />,
                        <Input
                            style={{ width: 130 }}
                            key='markingCode'
                            placeholder='Маркировка'
                            value={filter?.markingCode}
                            onChange={(data) => {
                                d(setFilter({ ...filter, markingCode: data.target.value }, filterContext));
                            }}
                        />,
                        <Input
                            style={{ width: 130 }}
                            key='awbNumber'
                            placeholder='AWB'
                            value={filter?.awbNumber}
                            onChange={(data) => {
                                d(setFilter({ ...filter, awbNumber: data.target.value }, filterContext));
                            }}
                        />,
                    ]}
                    onClear={() => d(setFilter(initFilter, filterContext))}
                />
            </div>
        );
    };

    const onSelectChange = (selectedRowKeys: React.Key[]) => {
        setSelectedRowKeys(selectedRowKeys);
    };

    const expandedRemainings = (record: IShipmentHeader) => {
        const columns: ColumnsType<IShipmentJournalLine> = [
            {
                title: 'Коробка',
                dataIndex: 'boxNumber',
                width: 250,
            },
            {
                title: 'Клиент',
                align: 'center',
                width: 150,
                render: (_, record) => {
                    return (
                        <>
                            {record.consigneeCode}
                            <TransitWarehouseTag warehouseCode={record.toWarehouseCode} />
                        </>
                    );
                },
            },

            {
                title: 'Маркировка',
                dataIndex: 'markingCode',
                width: 200,
            },
            {
                title: 'Продукт',
                dataIndex: 'itemName',
                width: 150,
                render: (_, record) => {
                    return (
                        <Tag
                            variant='solid'
                            color={record.itemColor}
                            style={{ marginBottom: 2, paddingBottom: 2, width: 'fit-content', fontSize: 15, fontWeight: 600 }}
                        >
                            <div>{record.itemName}</div>
                        </Tag>
                    );
                },
            },
            {
                title: 'Страна',
                dataIndex: 'countryName',
                width: 150,
            },
            {
                title: 'AWB',
                align: 'center',
                width: 130,
                render: (_, record) => {
                    return (
                        record.awbNumber && (
                            <Tag color='cyan' style={{ marginLeft: 10, color: '#000000' }}>
                                {record.awbNumber}
                            </Tag>
                        )
                    );
                },
            },
            {
                title: 'Приемка',
                render: (_: any, record: IShipmentJournalLine) => {
                    return getDateTimeLocal(record.acceptedOn);
                },
                align: 'center',
                width: 120,
            },
            {
                title: 'Отгрузка',
                align: 'center',
                width: 120,
                render: (_, record) => {
                    return record.shippedOn && <CheckOutlined />;
                },
            },
            {
                title: (
                    <Tooltip color='green' title='Температура принимаего груза'>
                        <Flex justify='center'>
                            <ThermometerInIcon style={{ fontSize: 14, marginBottom: 3 }} />
                            <div>&deg;C</div>
                        </Flex>
                    </Tooltip>
                ),
                align: 'center',
                width: 100,
                onCell: () => ({
                    style: {
                        background: '#97db75',
                        fontWeight: 600,
                    },
                }),
                render: (_, record) => {
                    return record.tempIn;
                },
            },
            {
                title: (
                    <Tooltip color='purple' title='Температура отгружаемого груза'>
                        <Flex justify='center'>
                            <ThermometerOutIcon style={{ fontSize: 14, marginBottom: 3 }} />
                            <div>&deg;C</div>
                        </Flex>
                    </Tooltip>
                ),
                align: 'center',
                width: 100,
                onCell: () => ({
                    style: {
                        background: '#d3adf7',
                        fontWeight: 600,
                    },
                }),
                render: (_, record) => {
                    return record.tempOut;
                },
            },
            {
                title: (
                    <>
                        Объем / м<sup>3</sup>
                    </>
                ),
                align: 'center',
                width: 120,
                onCell: () => ({
                    style: {
                        background: '#fafafa',
                        fontWeight: 600,
                    },
                }),
                render: (_: any, record: IShipmentJournalLine) => {
                    return record.calcVolume && record.calcVolume.toFixed(2);
                },
            },
            {
                title: 'Менеджер',
                dataIndex: 'createdByName',
                width: 250,
            },
            {},
        ];
        return (
            <Table
                rowKey='id'
                size='small'
                columns={columns}
                dataSource={record.lines}
                pagination={false}
                rowSelection={{
                    selectedRowKeys: selectedRowKeys,
                    onChange: onSelectChange,
                }}
            />
        );
    };

    const renderTable = () => {
        const columns: ColumnsType<IShipmentHeader> = [
            {
                title: 'Город доставки',
                dataIndex: 'cityName',
                width: 1060,
                onCell: () => ({
                    style: {
                        fontWeight: 600,
                    },
                }),
                render: (_, record) => {
                    return (
                        <Flex>
                            <span>{record.cityName}</span>
                            <Tags label={record.tag} />
                        </Flex>
                    );
                },
            },
            {
                title: (
                    <Tooltip color='purple' title='Температура отгружаемого груза'>
                        <Flex justify='center'>
                            <ThermometerOutIcon style={{ fontSize: 14, marginBottom: 3 }} />
                            <div>&deg;C</div>
                        </Flex>
                    </Tooltip>
                ),
                align: 'center',
                width: 100,
                onCell: () => ({
                    style: {
                        background: '#d3adf7',
                        fontWeight: 600,
                    },
                }),
                render: (_, record) => {
                    return record.tempOut;
                },
            },
            {
                title: 'Кол-во / шт',
                align: 'center',
                width: 120,
                dataIndex: 'totalQty',
                onCell: () => ({
                    style: {
                        background: '#fff8d5',
                        fontWeight: 700,
                    },
                }),
                render: (_, record) => {
                    return (
                        <>
                            <span style={{ color: record.totalScannedBoxQty === record.totalBoxQty ? '#228B22' : 'red' }}>
                                {record.totalScannedBoxQty}
                            </span>
                            <span> / </span>
                            <span>{record.totalBoxQty}</span>
                        </>
                    );
                },
            },
            {
                title: (
                    <>
                        Объем / м<sup>3</sup>
                    </>
                ),
                align: 'center',
                width: 120,
                onCell: () => ({
                    style: {
                        background: '#fff8d5',
                        fontWeight: 700,
                    },
                }),
                render: (_: any, record: IShipmentHeader) => {
                    return record.totalVolume.toFixed(2);
                },
            },
            {},
        ];

        return (
            <Table
                rowKey='cityKey'
                size='small'
                loading={{
                    spinning: loading,
                    indicator: <LoadingOutlined style={{ fontSize: 44 }} spin />,
                }}
                columns={columns}
                expandable={{
                    columnWidth: 32,
                    expandedRowRender: expandedRemainings,
                    defaultExpandedRowKeys: headers && headers.map((h) => h.cityName),
                }}
                dataSource={headers}
                pagination={false}
                scroll={{ y: height + (showFilter ? 0 : 15), x: 'max-content' }}
            />
        );
    };

    return (
        <div ref={containerRef} style={{ height: `calc(100vh - 490px - ${filterHeight}px)` }}>
            <Row>
                <FormHeader title={`Отгрузка`} />
            </Row>
            <Row>
                <Col span={12}>
                    {entity && (
                        <Form colon={false} labelCol={{ span: 8 }} wrapperCol={{ span: 16 }} labelWrap form={form}>
                            <Row gutter={18}>
                                <Col span={12}>
                                    <Form.Item label='Номер журнала' name='journalNumber'>
                                        <Input disabled={true} />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item label='Статус' name='status'>
                                        <Input disabled={true} />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={18}>
                                <Col span={12}>
                                    <Form.Item initialValue={entity?.deliveryOption} label='Вариант доставки' name='deliveryOption'>
                                        <Select
                                            disabled={
                                                loading ||
                                                entity?.status === JournalStatus.Completed ||
                                                entity?.status === JournalStatus.Cancelled
                                            }
                                            onChange={(value) => {
                                                onSetDeliveryOption(value);
                                            }}
                                            options={deliveryOptions.map((o) => {
                                                return { value: o.value, label: o.label };
                                            })}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    {entity.deliveryOption === DeliveryOption.Truck && (
                                        <Form.Item label='Номер машины' name='truckNumber'>
                                            <Input disabled={true} />
                                        </Form.Item>
                                    )}
                                </Col>
                            </Row>

                            {hasPermission(userSession.permissions, Permission.FinishShipmentJournal) &&
                                (entity.status === JournalStatus.New || entity.status === JournalStatus.InProcess) && (
                                    <Form.Item wrapperCol={{ offset: 11 }}>
                                        <Space size={'small'} style={{ float: 'right' }}>
                                            <Button
                                                type='primary'
                                                htmlType='submit'
                                                onClick={() => {
                                                    var noneTempOutHeader = headers.find((h) => h.tempOut == undefined);
                                                    if (noneTempOutHeader) {
                                                        error(api, 'Необходимо указать температуру отправляемого груза.');
                                                        return;
                                                    }

                                                    modal.confirm({
                                                        title: `Завершить журнал отгрузки?`,
                                                        content: 'Все неотгруженные коробки будут возвращены на остаток склада.',
                                                        icon: <QuestionCircleOutlined />,
                                                        okType: 'primary',
                                                        okText: 'ОК',
                                                        cancelText: 'Отмена',
                                                        onOk: () => {
                                                            onCompleteShipment();
                                                        },
                                                    });
                                                }}
                                                disabled={entity?.status === JournalStatus.New || loading}
                                            >
                                                Завершить
                                            </Button>
                                        </Space>
                                    </Form.Item>
                                )}

                            {entity.status === JournalStatus.Cancelled && (
                                <Form.Item label='Причина отмены' name='cancelReason' wrapperCol={{ span: 8 }}>
                                    <TextArea rows={6} disabled={true} />
                                </Form.Item>
                            )}
                        </Form>
                    )}
                </Col>
            </Row>

            {renderToolbar()}
            {renderFilter()}
            {renderTable()}

            <Row style={{ marginBottom: 15, marginTop: 15 }}>
                <Col span={24}>
                    <Space size={'small'} style={{ float: 'right' }}>
                        <Button type='default' onClick={() => navigate(`/warehouse/shipmentjournals`)}>
                            Закрыть
                        </Button>
                        {entity?.status === JournalStatus.New && (
                            <Button
                                type='primary'
                                onClick={() => {
                                    if (!hasPermission(userSession.permissions, Permission.ManageWarehouse)) {
                                        securityRestriction(api, [Permission.ManageWarehouse]);
                                        return;
                                    }

                                    var noneTempOutHeader = headers.find((h) => h.tempOut == undefined);
                                    if (noneTempOutHeader) {
                                        error(api, 'Необходимо указать температуру отправляемого груза.');
                                        return;
                                    }

                                    modal.confirm({
                                        title: `Отправить журнал в работу?`,
                                        icon: <QuestionCircleOutlined />,
                                        okType: 'primary',
                                        okText: 'ОК',
                                        cancelText: 'Отмена',
                                        onOk: () => {
                                            onSetToProcess();
                                        },
                                    });
                                }}
                                loading={loading}
                            >
                                Отправить в работу
                            </Button>
                        )}
                    </Space>
                </Col>
            </Row>

            <Modal
                width={400}
                title='Причина возврата коробок на остатки'
                open={openDeleteLinesModal}
                okText='ОК'
                closable={false}
                onOk={() => {
                    deleteLinesForm.submit();
                }}
                onCancel={() => {
                    deleteLinesForm.resetFields();
                    setOpenDeleteLinesModal(false);
                }}
            >
                <Form colon={false} wrapperCol={{ span: 24 }} form={deleteLinesForm} onFinish={onDeleteLines}>
                    <Form.Item name='ids' hidden>
                        <Input />
                    </Form.Item>
                    <Form.Item name='reason' required rules={[{ required: true, message: 'Укажите причину возврата' }]}>
                        <TextArea rows={6} />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                width={350}
                title='Указать температуру принимаего груза'
                destroyOnHidden={true}
                open={openTempOut}
                okText='ОК'
                closable={false}
                onOk={() => {
                    tempOutForm.submit();
                }}
                onCancel={() => {
                    tempOutForm.resetFields();
                    setOpenTempOut(false);
                    setRefreshRequired(true);
                }}
            >
                <Form
                    colon={false}
                    labelCol={{ span: 9 }}
                    wrapperCol={{ span: 15 }}
                    style={{ marginTop: 20 }}
                    form={tempOutForm}
                    onFinish={onSetTemIn}
                >
                    <Form.Item label='Температура'>
                        <Space.Compact>
                            <Form.Item noStyle name='tempOut' rules={[{ required: true, message: 'Укажите температуру.' }]}>
                                <InputNumber style={{ width: '100%' }} decimalSeparator=',' stringMode precision={1} />
                            </Form.Item>
                            <Space.Addon>&deg;C</Space.Addon>
                        </Space.Compact>
                    </Form.Item>
                </Form>
            </Modal>

            {contextHolder}
            {modalContextHolder}
        </div>
    );
};

export default ShipmentJournal;
